import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import * as THREE from 'three'
import { Delaunay } from 'd3-delaunay'
import type Star from '@/types/Star.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePlayerColorStore } from '@/stores/playerColorStore.ts'

interface PlayerTerritory {
  playerId: number
  stars: Star[]
  color: number
}

export const useMapTerritoryStore = defineStore('mapTerritoryStore', {
  state: () => ({
    territoryMesh: null as THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> | null,
    lastUpdateHash: '' as string,
    lastColorHash: '' as string,
    enabled: true as boolean,
    opacity: 0.2 as number,  // Default 20% opacity
    // Cache for efficient color-only updates: maps vertex index to playerId
    vertexToPlayerMap: [] as number[]
  }),
  actions: {
    initTerritoryMesh() {
      // Create empty geometry that we'll update later
      const geometry = new THREE.BufferGeometry()

      const material = new THREE.ShaderMaterial({
        vertexShader: `
          attribute vec3 vertexColor;
          varying vec3 vColor;

          void main() {
            vColor = vertexColor;
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          varying vec3 vColor;
          uniform float opacity;

          void main() {
            gl_FragColor = vec4(vColor, opacity);
          }
        `,
        uniforms: {
          opacity: { value: 0.2 }  // Default 20%
        },
        transparent: true,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide
      })

      this.territoryMesh = markRaw(new THREE.Mesh(geometry, material))
      this.territoryMesh.renderOrder = -1  // Behind everything else
      this.territoryMesh.frustumCulled = false

      return this.territoryMesh
    },

    /**
     * Update Voronoi territories based on star ownership
     * @param stars Map of all stars on the map
     */
    updateTerritories(stars: Map<number, Star>) {
      if (!this.territoryMesh) {
        return
      }

      // Group stars by owner
      const playerTerritories = this.groupStarsByOwner(stars)

      // Only generate territories for players that have stars
      if (playerTerritories.length === 0) {
        // Clear geometry if no territories
        this.territoryMesh.geometry.dispose()
        this.territoryMesh.geometry = new THREE.BufferGeometry()
        this.vertexToPlayerMap = []
        return
      }

      // Calculate hash to see if topology changed (positions + ownership)
      const currentHash = this.calculateStarHash(stars)
      const colorHash = this.calculateColorHash(playerTerritories)

      const topologyChanged = currentHash !== this.lastUpdateHash
      const colorsChanged = colorHash !== this.lastColorHash

      if (!topologyChanged && !colorsChanged) {
        return  // No changes at all
      }

      if (topologyChanged) {
        // Full regeneration needed - Voronoi diagram changed
        this.lastUpdateHash = currentHash
        this.lastColorHash = colorHash

        // Generate Voronoi diagram and convert to mesh
        const geometry = this.generateVoronoiGeometry(playerTerritories, stars)

        // Dispose old geometry and replace
        this.territoryMesh.geometry.dispose()
        this.territoryMesh.geometry = geometry
      } else if (colorsChanged) {
        // Only colors changed - just update the color attribute efficiently
        this.lastColorHash = colorHash
        this.updateVertexColors(playerTerritories)
      }
    },

    /**
     * Group stars by their owner playerId
     */
    groupStarsByOwner(stars: Map<number, Star>): PlayerTerritory[] {
      const playerMap = new Map<number, Star[]>()

      // Group stars by owner (including unowned stars as playerId 0)
      stars.forEach((star) => {
        if (!playerMap.has(star.playerId)) {
          playerMap.set(star.playerId, [])
        }
        playerMap.get(star.playerId)!.push(star)
      })

      // Convert to array and assign colors (skip unowned stars for rendering)
      const territories: PlayerTerritory[] = []

      playerMap.forEach((stars, playerId) => {
        if (playerId === 0) {
          // Include unowned stars in calculation but mark them for no rendering
          territories.push({ playerId: 0, stars, color: -1 })
        } else {
          const color = this.getPlayerColor(playerId)
          territories.push({ playerId, stars, color })
        }
      })

      return territories
    },

    /**
     * Get color for a player
     * Returns backend-assigned color if available, otherwise returns a subtle default color
     */
    getPlayerColor(playerId: number): number {
      const playerColorStore = usePlayerColorStore()

      // Try to get color from backend (WebSocket)
      const backendColor = playerColorStore.getColorForPlayer(playerId)
      if (backendColor) {
        // Convert hex string (e.g., "#FF0000") to number (e.g., 0xFF0000)
        return parseInt(backendColor.replace('#', ''), 16)
      }

      // No color assigned - use black
      return 0x000000
    },

    /**
     * Generate Voronoi geometry from player territories
     */
    generateVoronoiGeometry(territories: PlayerTerritory[], allStars: Map<number, Star>): THREE.BufferGeometry {
      // Collect all star positions for Voronoi calculation
      const points: number[] = []
      const starToPlayerMap: number[] = []  // Maps point index to playerId

      territories.forEach(({ playerId, stars }) => {
        stars.forEach((star) => {
          points.push(star.xPos, star.yPos)
          starToPlayerMap.push(playerId)
        })
      })

      // Calculate Voronoi diagram
      const delaunay = Delaunay.from(points.map((_, i) => i % 2 === 0 ? [points[i], points[i + 1]] : null).filter(Boolean) as [number, number][])
      const voronoi = delaunay.voronoi(this.getMapBounds(allStars))

      // Generate triangulated mesh for each cell
      const vertices: number[] = []
      const colors: number[] = []
      const vertexPlayerIds: number[] = []  // Track which player each vertex belongs to

      for (let i = 0; i < starToPlayerMap.length; i++) {
        const playerId = starToPlayerMap[i]
        const territory = territories.find(t => t.playerId === playerId)
        if (!territory) continue

        // Skip rendering for unowned stars (they're just spacers in the Voronoi)
        if (playerId === 0 || territory.color === -1) continue

        const cell = voronoi.cellPolygon(i)
        if (!cell || cell.length < 3) continue

        // Triangulate the polygon (simple fan triangulation from first vertex)
        // Manually parse hex to RGB to preserve sRGB color values
        // Note: We don't use THREE.Color() here because it auto-converts sRGB→linear,
        // which causes colors like orange (#ff7700) to render incorrectly
        const hexColor = territory.color
        const r = ((hexColor >> 16) & 255) / 255
        const g = ((hexColor >> 8) & 255) / 255
        const b = (hexColor & 255) / 255

        const firstVertex = cell[0]

        for (let j = 1; j < cell.length - 1; j++) {
          // Triangle: first vertex, current vertex, next vertex
          vertices.push(firstVertex[0], firstVertex[1], 0)
          colors.push(r, g, b)
          vertexPlayerIds.push(playerId)

          vertices.push(cell[j][0], cell[j][1], 0)
          colors.push(r, g, b)
          vertexPlayerIds.push(playerId)

          vertices.push(cell[j + 1][0], cell[j + 1][1], 0)
          colors.push(r, g, b)
          vertexPlayerIds.push(playerId)
        }
      }

      // Cache the vertex-to-player mapping for efficient color updates
      this.vertexToPlayerMap = vertexPlayerIds

      // Create BufferGeometry
      const geometry = new THREE.BufferGeometry()
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3))
      geometry.setAttribute('vertexColor', new THREE.Float32BufferAttribute(colors, 3))

      return geometry
    },

    /**
     * Calculate bounds for Voronoi diagram clipping
     */
    getMapBounds(stars: Map<number, Star>): [number, number, number, number] {
      const gameStore = useGameStore()

      // Use actual galaxy bounds if available
      if (gameStore.galaxyTileGridSize) {
        const size = gameStore.galaxyTileGridSize
        return [0, 0, size, size]
      }

      // Fallback: calculate from star positions
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity

      stars.forEach((star) => {
        minX = Math.min(minX, star.xPos)
        minY = Math.min(minY, star.yPos)
        maxX = Math.max(maxX, star.xPos)
        maxY = Math.max(maxY, star.yPos)
      })

      // Add minimal padding (5% on each side) to avoid edge artifacts
      const paddingX = (maxX - minX) * 0.05
      const paddingY = (maxY - minY) * 0.05

      return [minX - paddingX, minY - paddingY, maxX + paddingX, maxY + paddingY]
    },

    /**
     * Calculate a hash of star positions and owners to detect changes
     * NOTE: Does NOT include colors - colors can be updated separately without rebuilding geometry
     */
    calculateStarHash(stars: Map<number, Star>): string {
      const data: string[] = []
      stars.forEach((star) => {
        if (star.playerId !== 0) {
          data.push(`${star.xPos},${star.yPos},${star.playerId}`)
        }
      })
      return data.sort().join('|')
    },

    /**
     * Calculate color hash from player territories
     */
    calculateColorHash(territories: PlayerTerritory[]): string {
      const data: string[] = []
      territories.forEach(({ playerId, color }) => {
        data.push(`${playerId}:${color}`)
      })
      return data.sort().join('|')
    },

    /**
     * Update only vertex colors without regenerating geometry
     * Efficiently updates the color buffer when player colors change
     */
    updateVertexColors(territories: PlayerTerritory[]) {
      if (!this.territoryMesh || !this.territoryMesh.geometry) {
        return
      }

      const geometry = this.territoryMesh.geometry
      const colorAttribute = geometry.getAttribute('vertexColor') as THREE.BufferAttribute

      if (!colorAttribute || this.vertexToPlayerMap.length === 0) {
        // No cached mapping - skip update (will regenerate next time)
        return
      }

      // Build a quick lookup map of playerId -> color
      const playerColorMap = new Map<number, { r: number; g: number; b: number }>()
      territories.forEach(({ playerId, color }) => {
        const r = ((color >> 16) & 255) / 255
        const g = ((color >> 8) & 255) / 255
        const b = (color & 255) / 255
        playerColorMap.set(playerId, { r, g, b })
      })

      // Update each vertex color based on its playerId
      const colors = colorAttribute.array as Float32Array
      for (let i = 0; i < this.vertexToPlayerMap.length; i++) {
        const playerId = this.vertexToPlayerMap[i]
        const color = playerColorMap.get(playerId)

        if (color) {
          colors[i * 3] = color.r
          colors[i * 3 + 1] = color.g
          colors[i * 3 + 2] = color.b
        }
      }

      // Mark the attribute as needing update
      colorAttribute.needsUpdate = true
    },

    /**
     * Show or hide the territory overlay
     */
    setVisible(visible: boolean) {
      if (this.territoryMesh) {
        this.territoryMesh.visible = visible
      }
    },

    /**
     * Set territory opacity
     */
    setOpacity(opacity: number) {
      this.opacity = Math.max(0, Math.min(1, opacity))
      if (this.territoryMesh && this.territoryMesh.material.uniforms.opacity) {
        this.territoryMesh.material.uniforms.opacity.value = this.opacity
      }
    },

    /**
     * Clean up resources
     */
    dispose() {
      if (this.territoryMesh) {
        this.territoryMesh.geometry.dispose()
        if (this.territoryMesh.material) {
          this.territoryMesh.material.dispose()
        }
      }
    }
  }
})
